import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { InsightsReportDTO, InsightsReportUpdateDTO } from "groopit-js/client";
import React, { useCallback, useState } from "react";
import { useTimeoutFn } from "react-use";
import {
  canManage,
  emailReport,
  getFullReportContentAsText,
  getReportContent,
  getReportCreator,
  getReportDate,
  getReportFilter,
  getReportTitle,
} from "./InsightsReport";
import { useGroopitApiContext, useGroupContext } from "groopit-js";
import { useInsightsPanelContext } from "./InsightsPanelContext";
import { EditIcon } from "../Icons/EditIcon";
import { CopyIcon } from "../Icons/CopyIcon";
import { PublishIcon } from "../Icons/PublishIcon";
import { PromoteIcon } from "../Icons/PromoteIcon";
import { CloseIcon } from "../Icons/CloseIcon";
import { ViewIcon } from "../Icons/ViewIcon";
import { DeleteIcon } from "../Icons/DeleteIcon";
import useLatestRef from "../react/react-use/useLatestRef";
import "./InsightsReportViewer.css";
import { MoreVert } from "@material-ui/icons";
import { FakeStreamingMarkdown } from "../Ai/ChatWithYourData/FakeStreamingMarkdown";

interface InsightsReportViewerProps {
  report: InsightsReportDTO;
  isThumbnailView: boolean;
}

export const InsightsReportViewer = (
  props: InsightsReportViewerProps,
): JSX.Element => {
  const { report, isThumbnailView } = { ...props };
  const insightsPanelContext = useInsightsPanelContext();
  const api = useLatestRef(useGroopitApiContext());
  const { user_id, owner } = useGroupContext();
  const [reportMenuAnchor, setReportMenuAnchor] = useState<HTMLElement>();
  const isReportMenuOpen = Boolean(reportMenuAnchor);
  const [reportCopiedTooltipTimeout, setReportCopiedTooltipTimeout] = useState<
    number | undefined
  >();
  const showReportCopiedTooltip = Boolean(reportCopiedTooltipTimeout);
  const subtitleClasses = isThumbnailView
    ? "italic text-display-subtitle"
    : "italic";
  const buttonSizes = isThumbnailView ? "small" : "medium";
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  useTimeoutFn(
    () => setReportCopiedTooltipTimeout(undefined),
    reportCopiedTooltipTimeout,
  );

  const copyReport = useCallback(() => {
    navigator.clipboard.writeText(getFullReportContentAsText(report));
    setReportCopiedTooltipTimeout(3000);
  }, [report]);

  const unpublishReport = useCallback(() => {
    api.current.insights
      .updateInsightsReport(
        report.id,
        new InsightsReportUpdateDTO({
          ...report,
          isPublished: false,
        }),
      )
      .then((updatedReport) => {
        insightsPanelContext.onReportUpdated(updatedReport);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [api, report, insightsPanelContext]);

  const publishReport = useCallback(() => {
    api.current.insights
      .updateInsightsReport(
        report.id,
        new InsightsReportUpdateDTO({
          ...report,
          isPublished: true,
        }),
      )
      .then((updatedReport) => {
        insightsPanelContext.onReportUpdated(updatedReport);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [api, report, insightsPanelContext]);

  const deleteReport = useCallback(() => {
    api.current.insights
      .deleteInsightsReport(report.id)
      .then(() => insightsPanelContext.onReportDeleted(report.id))
      .catch((error) => {
        console.error(error);
      });
  }, [api, insightsPanelContext, report.id]);

  return (
    <div
      className={`${
        isThumbnailView &&
        "insights-thumbnail h-80 overflow-hidden cursor-pointer"
      } ${report.isPublished && "published"} text-color-secondary`}
    >
      <div
        className={`border-app-slateblue ${
          !isThumbnailView && "border-b-2"
        } pb-2`}
      >
        <div className="flex">
          <div className="flex-grow">
            <div className="text-display-headline" style={{ paddingTop: 0 }}>
              {getReportTitle(report)}
            </div>
            <div className={subtitleClasses}>{getReportCreator(report)}</div>
            <div className={subtitleClasses}>{getReportDate(report)}</div>
            {!isThumbnailView && (
              <div className={subtitleClasses}>{getReportFilter(report)}</div>
            )}
          </div>
          <div className="flex flex-col text-app-slateblue">
            {!isThumbnailView && (
              <IconButton
                onClick={() => insightsPanelContext.setSelectedReport(null)}
                size={buttonSizes}
                aria-label="Close"
              >
                <CloseIcon className="text-app-gray-02" />
              </IconButton>
            )}
            <Tooltip
              title="Report copied to clipboard"
              open={showReportCopiedTooltip}
            >
              <IconButton
                id="filter-data-menu-button"
                className="place-self-start"
                color="inherit"
                size={buttonSizes}
                aria-controls="filter-data-menu"
                aria-haspopup="true"
                aria-expanded={isReportMenuOpen ? "true" : undefined}
                aria-label="Filter Data"
                onClick={(event) => {
                  setReportMenuAnchor(event.currentTarget);
                  event.stopPropagation();
                }}
              >
                <MoreVert />
              </IconButton>
            </Tooltip>
            <Menu
              id="filter-data-menu"
              anchorEl={reportMenuAnchor}
              open={isReportMenuOpen}
              onClick={(event) => event.stopPropagation()}
              onClose={() => setReportMenuAnchor(undefined)}
              MenuListProps={{
                "aria-labelledby": "filter-data-menu-button",
              }}
            >
              {isThumbnailView && (
                <MenuItem
                  onClick={(event) => {
                    insightsPanelContext.setSelectedReport({
                      report,
                      isEditMode: false,
                      isNewlyCreated: false,
                    });
                    setReportMenuAnchor(undefined);
                    event.stopPropagation();
                  }}
                >
                  <ListItemIcon>
                    <ViewIcon className="text-app-gray-02" />
                  </ListItemIcon>
                  <Typography>View</Typography>
                </MenuItem>
              )}
              {canManage(report, user_id, owner) && (
                <MenuItem
                  onClick={(event) => {
                    insightsPanelContext.setSelectedReport({
                      report,
                      isEditMode: true,
                      isNewlyCreated: false,
                    });
                    setReportMenuAnchor(undefined);
                    event.stopPropagation();
                  }}
                >
                  <ListItemIcon>
                    <EditIcon className="text-app-gray-02" />
                  </ListItemIcon>
                  <Typography>Edit</Typography>
                </MenuItem>
              )}
              <MenuItem
                onClick={(event) => {
                  copyReport();
                  setReportMenuAnchor(undefined);
                  event.stopPropagation();
                }}
              >
                <ListItemIcon>
                  <CopyIcon className="text-app-gray-02" />
                </ListItemIcon>
                <Typography>Copy</Typography>
              </MenuItem>
              {report.isPublished && canManage(report, user_id, owner) && (
                <MenuItem
                  onClick={(event) => {
                    unpublishReport();
                    setReportMenuAnchor(undefined);
                    event.stopPropagation();
                  }}
                >
                  <ListItemIcon>
                    <PublishIcon className="text-app-gray-02" />
                  </ListItemIcon>
                  <Typography>Unpublish</Typography>
                </MenuItem>
              )}
              {!report.includesHiddenData &&
                (report.isPublished ? (
                  <MenuItem
                    onClick={(event) => {
                      emailReport(report);
                      setReportMenuAnchor(undefined);
                      event.stopPropagation();
                    }}
                  >
                    {canManage(report, user_id, owner) ? (
                      <>
                        <ListItemIcon>
                          <PromoteIcon className="text-app-gray-02" />
                        </ListItemIcon>
                        <Typography>Promote</Typography>
                      </>
                    ) : (
                      <>
                        <ListItemIcon>
                          <PublishIcon className="text-app-gray-02" />
                        </ListItemIcon>
                        <Typography>Send</Typography>
                      </>
                    )}
                  </MenuItem>
                ) : (
                  canManage(report, user_id, owner) &&
                  owner && (
                    <MenuItem
                      onClick={(event) => {
                        publishReport();
                        setReportMenuAnchor(undefined);
                        event.stopPropagation();
                      }}
                    >
                      <ListItemIcon>
                        <PublishIcon className="text-app-gray-02" />
                      </ListItemIcon>
                      <Typography>Publish</Typography>
                    </MenuItem>
                  )
                ))}
              {canManage(report, user_id, owner) && (
                <MenuItem
                  onClick={(event) => {
                    setDeleteConfirmationOpen(true);
                    setReportMenuAnchor(undefined);
                    event.stopPropagation();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon className="text-app-gray-02" />
                  </ListItemIcon>
                  Delete
                </MenuItem>
              )}
            </Menu>
            {canManage(report, user_id, owner) && (
              <Dialog
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(false)}
                aria-labelledby="delete-confirmation-title"
                aria-describedby="delete-confirmation-description"
              >
                <DialogTitle id="delete-confirmation-title">Delete</DialogTitle>
                <DialogContent>
                  <DialogContentText id="delete-confirmation-description">
                    Deleting this summary will remove it for you and everyone in
                    your group.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    color="default"
                    variant="outlined"
                    onClick={(event) => {
                      setDeleteConfirmationOpen(false);
                      event.stopPropagation();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={(event) => {
                      deleteReport();
                      setDeleteConfirmationOpen(false);
                      event.stopPropagation();
                    }}
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 whitespace-pre-wrap">
        <FakeStreamingMarkdown
          content={getReportContent(report)}
          shouldFakeStreaming={false}
        />
      </div>
    </div>
  );
};
